import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Option, Period } from '@spike/model';
import clsx from 'clsx';
import padStart from 'lodash/padStart';
import moment from 'moment-timezone';
import React from 'react';
import Calendar from 'react-calendar';
import styles from './WeekPicker.module.scss';

interface WeekPickerProps {
    timeZone: string;
    week: Period;
    onChange: (week: Period) => void;
}

const weekOptions: Array<Option<number>> = [
    { id: 0, name: 'This week' },
    { id: 1, name: 'Next week' },
    { id: 2, name: 'In 2 weeks' },
    { id: 3, name: 'In 3 weeks' },
    { id: 4, name: 'In 4 weeks' },
    { id: 5, name: 'In 5 weeks' },
    { id: 6, name: 'In 6 weeks' },
    { id: 7, name: 'In 7 weeks' },
    { id: 8, name: 'In 8 weeks' }
];

export const WeekPicker: React.FC<WeekPickerProps> = ({ timeZone, week, onChange }) => {
    const changeWeekHandler = (selectedWeek: Period) => {
        onChange({
            from: selectedWeek.from.clone(),
            to: selectedWeek.to.clone()
        });
    };

    const selectDateHandler = (date: Date) => {
        const from = moment
            .tz(
                `${date.getFullYear()}${padStart((date.getMonth() + 1).toString(), 2, '0')}${date.getDate()}`,
                'YYYYMMDD',
                timeZone
            )
            .startOf('week');
        const to = from.clone().endOf('week');
        changeWeekHandler({ from, to });
    };

    const onSelectWeekHandler = (weeks: number) => {
        const thisWeekMonday = moment.tz(timeZone).startOf('week');
        const selectedWeek = {
            from: thisWeekMonday.clone().add(weeks, 'weeks'),
            to: thisWeekMonday.clone().add(weeks, 'weeks').endOf('week')
        };
        changeWeekHandler(selectedWeek);
    };

    // Fix selection highlighting by comparing week start dates
    const currentWeekStart = moment.tz(timeZone).startOf('week');
    const selectedWeekStart = week.from.clone().startOf('week');
    const isOptionSelected = (weeks: number): boolean => {
        const optionWeekStart = currentWeekStart.clone().add(weeks, 'weeks');
        return optionWeekStart.isSame(selectedWeekStart, 'day');
    };

    return (
        <div className={styles.container}>
            <div className={styles.leftContainer}>
                <div className={styles.weekOptionsList}>
                    {weekOptions.map(option => {
                        const isSelected = isOptionSelected(option.id);
                        return (
                            <div
                                key={option.id}
                                className={clsx(styles.weekOptionItem, {
                                    [styles.weekOptionItemSelected]: isSelected
                                })}
                                onClick={() => onSelectWeekHandler(option.id)}
                            >
                                <span
                                    className={clsx(styles.weekOptionLabel, {
                                        [styles.weekOptionLabelSelected]: isSelected
                                    })}
                                >
                                    {option.name}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={styles.rightContainer}>
                <Calendar
                    calendarType="US"
                    onClickDay={selectDateHandler}
                    value={[
                        week.from.clone().add(12, 'hours').toDate(),
                        week.to.clone().subtract(12, 'hours').toDate()
                    ]}
                    showFixedNumberOfWeeks={true}
                    className={styles.calendar}
                    nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
                    prevLabel={<FontAwesomeIcon icon={faChevronLeft} />}
                    prev2Label={null}
                    next2Label={null}
                    showNeighboringMonth={false}
                    locale={'en'}
                    formatShortWeekday={(locale, date) => moment(date).format('dd').substring(0, 1)}
                    formatMonth={(locale, date) => moment(date).format('MMM')}
                />
            </div>
        </div>
    );
};

export default WeekPicker;
